/**
 * CivicBeat Data Access Layer
 *
 * This module provides type-safe helpers for interacting with the CivicBeat
 * personalization data model in Supabase. It handles:
 *
 * - profiles: User profile information (username, timestamps)
 * - user_preferences: User's selected topics/interests for personalization
 *
 * All helpers gracefully handle errors and allow guest users (null userId).
 * These functions are designed to work alongside existing mock data during migration.
 */

import { supabase } from './supabaseClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * User profile - basic account information
 */
export interface Profile {
  id: string;
  username: string | null;
  display_name: string | null;
  country: string | null;
  role: 'user' | 'admin' | 'editor';
  is_admin: boolean;
  is_disabled: boolean;
  deleted_at: string | null;
  created_at: string;
  updated_at: string | null;
}

/**
 * User preferences - personalization settings
 */
export interface UserPreferences {
  id: string;
  user_id: string;
  topics: string[];
  created_at: string;
}


// ============================================================================
// PROFILE HELPERS
// ============================================================================

/**
 * Get or create a user profile
 *
 * If the profile exists, returns it. If not, creates a new profile with
 * optional username. Safe to call multiple times - idempotent operation.
 *
 * @param userId - The user's ID (from auth.users)
 * @param username - Optional username to set on creation
 * @returns The profile record or null on error
 */
export async function getOrCreateProfile(
  userId: string,
  username?: string
): Promise<Profile | null> {
  try {
    // Try to get existing profile
    const { data: existing, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('[civicbeatData] Error fetching profile:', fetchError);
      return null;
    }

    // If profile exists, return it
    if (existing) {
      console.log('[civicbeatData] Loaded profile:', {
        id: existing.id,
        email: existing.email,
        role: existing.role,
        is_admin: existing.is_admin,
        is_disabled: existing.is_disabled
      });
      return existing as Profile;
    }

    // Create new profile
    const { data: newProfile, error: insertError } = await supabase
      .from('profiles')
      .insert({
        id: userId,
        display_name: username || null,
        created_at: new Date().toISOString(),
        updated_at: null,
      })
      .select()
      .single();

    if (insertError) {
      console.error('[civicbeatData] Error creating profile:', insertError);
      console.error('[civicbeatData] Error code:', insertError.code);
      console.error('[civicbeatData] Error message:', insertError.message);
      console.error('[civicbeatData] Error details:', insertError.details);
      console.error('[civicbeatData] Error hint:', insertError.hint);
      return null;
    }

    return newProfile as Profile;
  } catch (err) {
    console.error('[civicbeatData] Unexpected error in getOrCreateProfile:', err);
    return null;
  }
}

/**
 * Get user profile by ID
 *
 * @param userId - The user's ID
 * @returns The profile record or null on error
 */
export async function getProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[civicbeatData] Error fetching profile:', error);
      return null;
    }

    return data as Profile;
  } catch (err) {
    console.error('[civicbeatData] Unexpected error in getProfile:', err);
    return null;
  }
}

/**
 * Update user profile fields
 *
 * @param userId - The user's ID
 * @param updates - Partial profile data to update
 * @returns The updated profile or null on error
 */
export async function updateProfile(
  userId: string,
  updates: Partial<Pick<Profile, 'display_name' | 'country'>>
): Promise<Profile | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      console.error('[civicbeatData] Error updating profile:', error);
      return null;
    }

    return data as Profile;
  } catch (err) {
    console.error('[civicbeatData] Unexpected error in updateProfile:', err);
    return null;
  }
}

// ============================================================================
// USER PREFERENCES HELPERS
// ============================================================================

/**
 * Get user's topic preferences
 *
 * Returns the user's selected topics for personalization. Returns null if
 * no preferences have been set yet.
 *
 * @param userId - The user's ID
 * @returns The user preferences or null if not found/error
 */
export async function getUserPreferences(
  userId: string
): Promise<UserPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('interests')
      .eq('id', userId)
      .maybeSingle();

    if (error) {
      console.error('[civicbeatData] Error fetching user preferences:', error);
      return null;
    }

    if (!data) {
      console.log('[getUserPreferences] No profile found for user:', userId);
      return null;
    }

    console.log('[getUserPreferences] Fetched interests from DB:', data.interests);

    return {
      user_id: userId,
      topics: data.interests || [],
      created_at: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[civicbeatData] Unexpected error in getUserPreferences:', err);
    return null;
  }
}

/**
 * Create or update user's topic preferences
 *
 * Upserts the user's selected topics. This is called after onboarding or
 * when the user updates their interests in settings.
 *
 * @param userId - The user's ID
 * @param topics - Array of topic strings (e.g., ["Justice", "Environment"])
 * @returns The updated preferences or null on error
 */
export async function upsertUserPreferences(
  userId: string,
  topics: string[]
): Promise<UserPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .update({
        interests: topics,
        updated_at: new Date().toISOString(),
      })
      .eq('id', userId)
      .select('interests')
      .single();

    if (error) {
      console.error('[civicbeatData] Error upserting user preferences:', error);
      return null;
    }

    return {
      user_id: userId,
      topics: data.interests || [],
      created_at: new Date().toISOString(),
    };
  } catch (err) {
    console.error('[civicbeatData] Unexpected error in upsertUserPreferences:', err);
    return null;
  }
}
